<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { parse, format } from 'date-fns'
import { fromZonedTime, formatInTimeZone } from 'date-fns-tz'
import { IncidentReportType } from '@roadfootage/api-client'
import { useIncidentSubmissionStore } from '../../stores/incident-submission'
import { detectIncidentType } from '../../utils/incident-detection'
import { getTimezoneFromCoordinates, getBrowserTimezones } from '../../utils/timezone'
import * as limits from '../../utils/limits'
import {
  isRequired,
  hasMaxLength,
  isDateNotInFuture,
  isAddressPicked
} from '../../utils/validation'
import { formatIncidentType } from '../../utils/format'

import AddressAutocomplete from '../forms/AddressAutocomplete.vue'

defineExpose({ validate })

const incidentStore = useIncidentSubmissionStore()
const { incidentForm } = storeToRefs(incidentStore)

// --- Options (Now with icons for the UI cards) ---
const incidentTypeOptions = [
  {
    label: formatIncidentType(IncidentReportType.TrafficAccident),
    value: IncidentReportType.TrafficAccident,
    icon: 'mdi-tow-truck'
  },
  {
    label: formatIncidentType(IncidentReportType.BadDriving),
    value: IncidentReportType.BadDriving,
    icon: 'mdi-alert-octagon'
  },
  {
    label: formatIncidentType(IncidentReportType.RoadRage),
    value: IncidentReportType.RoadRage,
    icon: 'mdi-emoticon-angry'
  },
  {
    label: formatIncidentType(IncidentReportType.LawEnforcement),
    value: IncidentReportType.LawEnforcement,
    icon: 'mdi-police-badge'
  }
]

// --- Date / Time Handling ---
const localDate = ref<string>('')
const localTime = ref<string>('')
// True once the component has finished its initial hydration from store state.
// Used to distinguish programmatic initialisation from user edits when clearing hints.
const isMounted = ref(false)

onMounted(() => {
  if (incidentForm.value.incidentReportType) {
    hasManuallySelectedType.value = true
  }

  if (incidentForm.value.incidentDateTime) {
    const parsedDate = new Date(incidentForm.value.incidentDateTime)

    // Fallback to browser timezone just in case it's an old draft without one
    const targetTz =
      incidentForm.value.incidentTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone

    // Force the strings to render in the target timezone, completely ignoring the browser's local clock
    localDate.value = formatInTimeZone(parsedDate, targetTz, 'yyyy/MM/dd')
    localTime.value = formatInTimeZone(parsedDate, targetTz, 'HH:mm')
  }
  nextTick(() => {
    isMounted.value = true
  })
})

// Validation
const formRef = ref()

// focus: move keyboard focus to the first invalid field.
function validate(focus = false) {
  return formRef.value?.validate?.(focus) ?? true
}

// Re-check while the user edits. validate(false) skips QForm's focus-the-first-invalid-field step, which
// would otherwise yank focus (and open the mobile address dialog) mid-keystroke.
function revalidate() {
  return formRef.value?.validate?.(false) ?? true
}

// --- Automagic Detection Logic ---
const hasManuallySelectedType = ref(false)
let debounceTimer: ReturnType<typeof setTimeout>

function selectIncidentType(type: IncidentReportType) {
  incidentForm.value.incidentReportType = type
  hasManuallySelectedType.value = true // Lock the choice so the automagic watcher stops overriding them
}

watch(
  () => incidentForm.value.userSubmittedIncidentDescription,
  (newVal) => {
    if (isMounted.value) {
      incidentForm.value.extractedMetadataHints.description = false
    }

    if (hasManuallySelectedType.value) return

    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
      const detected = detectIncidentType(newVal)
      if (detected && !hasManuallySelectedType.value && !incidentForm.value.incidentReportType) {
        incidentForm.value.incidentReportType = detected
        revalidate()
      }
    }, 600)
  }
)

watch(
  () => [localDate.value, localTime.value, incidentForm.value.incidentTimezone],
  ([newDate, newTime, newTz]) => {
    if (newDate && newTime && newTz) {
      const parsedLocal = parse(`${newDate} ${newTime}`, 'yyyy/MM/dd HH:mm', new Date())
      const rawIsoString = format(parsedLocal, "yyyy-MM-dd'T'HH:mm:ss")
      incidentForm.value.incidentDateTime = fromZonedTime(rawIsoString, newTz)
    } else {
      incidentForm.value.incidentDateTime = null
    }
    // Clear the auto-detected hint once the user has manually changed the date/time
    if (isMounted.value) {
      incidentForm.value.extractedMetadataHints.dateTime = false
    }
    revalidate()
  }
)
const dateTimeHint = computed(() =>
  incidentForm.value.extractedMetadataHints.dateTime === 'dashcam'
    ? 'Time from your dashcam — please review'
    : 'Date and time auto-detected from footage — please review'
)

// --- Timezone Handling ---
const timezoneOptions = ref<string[]>(getBrowserTimezones())
const filteredTimezoneOptions = ref<string[]>(timezoneOptions.value)

function filterTimezones(val: string, update: (callback: () => void) => void) {
  update(() => {
    const needle = val.toLowerCase()
    filteredTimezoneOptions.value = timezoneOptions.value.filter((v) =>
      v.toLowerCase().includes(needle)
    )
  })
}

// --- Location Autocomplete ---
function handleLocationSelected(coords: { lat: number; lon: number }) {
  incidentForm.value.localLatitude = coords.lat
  incidentForm.value.localLongitude = coords.lon
  incidentStore.updateGeoJsonLocation()

  const tz = getTimezoneFromCoordinates(coords.lat, coords.lon)
  if (tz) {
    incidentForm.value.incidentTimezone = tz
  }
  // Clear the auto-detected hint — user has now explicitly chosen a location
  incidentForm.value.extractedMetadataHints.location = false
  revalidate()
}

// A cleared address must not keep the previous pin, or the report would carry coordinates for text the user removed.
function handleLocationCleared() {
  incidentForm.value.localLatitude = null
  incidentForm.value.localLongitude = null
  incidentStore.updateGeoJsonLocation()
}
</script>

<template>
  <div class="row justify-center">
    <div class="col-12" style="max-width: 600px">
      <div class="q-mb-lg text-center">
        <h6 class="text-h6 q-mt-none q-mb-xs text-blue-grey-9" tabindex="-1" data-step-heading>
          Incident Details
        </h6>
        <p class="text-body2 text-blue-grey-8">
          Tell us what happened. Keep it simple and factual.
        </p>
      </div>

      <q-form ref="formRef" class="q-gutter-y-md">
        <div
          v-if="incidentForm.extractedMetadataHints.description"
          class="auto-detect-hint row items-center no-wrap q-gutter-x-xs q-mb-xs"
        >
          <q-icon name="mdi-auto-fix" size="xs" color="blue-8" />
          <span class="text-caption text-blue-9"
            >Description suggested from footage — please review and expand</span
          >
        </div>

        <q-input
          v-model="incidentForm.userSubmittedIncidentDescription"
          type="textarea"
          outlined
          label="Describe what happened"
          label-color="blue-grey-9"
          color="blue-8"
          autogrow
          :rules="[
            isRequired('Description is required'),
            hasMaxLength(
              limits.userSubmittedIncidentDescription,
              `Description must not exceed ${limits.userSubmittedIncidentDescription} characters.`
            )
          ]"
        />

        <q-field
          v-model="incidentForm.incidentReportType"
          :rules="[isRequired('Please select an incident type')]"
          borderless
          dense
          class="q-pb-none"
        >
          <template v-slot:control>
            <div class="full-width">
              <div
                id="incident-type-label"
                class="text-caption text-weight-bold text-blue-grey-9 q-mb-sm"
              >
                What type of incident was it?
              </div>

              <q-scroll-area
                style="height: 105px"
                class="full-width"
                :thumb-style="{ opacity: '0.2' }"
              >
                <div
                  class="row no-wrap q-gutter-x-md q-pb-sm q-px-xs justify-center"
                  role="radiogroup"
                  aria-labelledby="incident-type-label"
                >
                  <q-card
                    v-for="option in incidentTypeOptions"
                    :key="option.value"
                    v-ripple
                    flat
                    bordered
                    class="incident-card cursor-pointer transition-border text-center flex flex-center"
                    :class="
                      incidentForm.incidentReportType === option.value
                        ? 'border-blue bg-blue-1'
                        : 'bg-white'
                    "
                    role="radio"
                    tabindex="0"
                    :aria-checked="incidentForm.incidentReportType === option.value"
                    @click="selectIncidentType(option.value)"
                    @keydown.enter.prevent="selectIncidentType(option.value)"
                    @keydown.space.prevent="selectIncidentType(option.value)"
                  >
                    <q-card-section class="q-pa-sm">
                      <q-icon
                        :name="option.icon"
                        size="lg"
                        :color="
                          incidentForm.incidentReportType === option.value
                            ? 'blue-8'
                            : 'blue-grey-4'
                        "
                        class="transition-color"
                      />
                      <div
                        class="text-caption q-mt-xs transition-color"
                        style="line-height: 1.1"
                        :class="
                          incidentForm.incidentReportType === option.value
                            ? 'text-blue-10 text-weight-bold'
                            : 'text-blue-grey-8'
                        "
                      >
                        {{ option.label }}
                      </div>
                    </q-card-section>
                  </q-card>
                </div>
              </q-scroll-area>
            </div>
          </template>
        </q-field>

        <q-separator class="q-my-sm" />

        <div class="text-caption text-uppercase text-weight-bold text-blue-grey-7 q-mb-sm">
          Location & Time
        </div>

        <div
          v-if="incidentForm.extractedMetadataHints.location"
          class="auto-detect-hint row items-center no-wrap q-gutter-x-xs q-mb-xs"
        >
          <q-icon name="mdi-auto-fix" size="xs" color="blue-8" />
          <span class="text-caption text-blue-9">Location from your dashcam — please review</span>
        </div>

        <address-autocomplete
          v-model="incidentForm.address"
          @location-selected="handleLocationSelected"
          @location-cleared="handleLocationCleared"
          :rules="[
            isRequired('Location is required.'),
            isAddressPicked(() => incidentForm.location !== null)
          ]"
        />

        <q-select
          v-model="incidentForm.incidentTimezone"
          :options="filteredTimezoneOptions"
          outlined
          use-input
          fill-input
          hide-selected
          label="Timezone of Incident"
          label-color="blue-grey-9"
          color="blue-8"
          class="q-mt-sm"
          :rules="[
            isRequired('Timezone is required'),
            hasMaxLength(limits.timezone, `Timezone must not exceed ${limits.timezone} characters.`)
          ]"
          @filter="filterTimezones"
        >
          <template v-slot:prepend>
            <q-icon name="mdi-earth" color="blue-grey-5" />
          </template>
        </q-select>

        <div
          v-if="incidentForm.extractedMetadataHints.dateTime"
          class="auto-detect-hint row items-center no-wrap q-gutter-x-xs q-mb-xs q-mt-sm"
        >
          <q-icon name="mdi-auto-fix" size="xs" color="blue-8" />
          <span class="text-caption text-blue-9">{{ dateTimeHint }}</span>
        </div>

        <div class="row q-col-gutter-x-md q-mt-sm">
          <div class="col-12 col-sm-6 q-mb-md-none q-mb-md">
            <q-input
              v-model="localDate"
              outlined
              label="Date of Incident"
              label-color="blue-grey-9"
              color="blue-8"
              mask="date"
              :rules="[
                isRequired('Date is required'),
                isDateNotInFuture('Date cannot be in the future.')
              ]"
            >
              <template v-slot:prepend>
                <q-icon
                  name="mdi-calendar"
                  class="cursor-pointer transition-color hover-blue"
                  color="blue-grey-5"
                >
                  <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                    <q-date v-model="localDate" color="blue-8">
                      <div class="row items-center justify-end">
                        <q-btn v-close-popup label="Close" color="blue-8" flat />
                      </div>
                    </q-date>
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>

          <div class="col-12 col-sm-6">
            <q-input
              v-model="localTime"
              outlined
              label="Time of Incident"
              label-color="blue-grey-9"
              color="blue-8"
              mask="time"
              :rules="[isRequired('Time is required')]"
            >
              <template v-slot:prepend>
                <q-icon
                  name="mdi-clock-outline"
                  class="cursor-pointer transition-color hover-blue"
                  color="blue-grey-5"
                >
                  <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                    <q-time v-model="localTime" color="blue-8" format24h>
                      <div class="row items-center justify-end">
                        <q-btn v-close-popup label="Close" color="blue-8" flat />
                      </div>
                    </q-time>
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
        </div>
      </q-form>
    </div>
  </div>
</template>

<style scoped>
:deep(.q-field__label) {
  font-weight: 500;
}

/* Card Styling */
.incident-card {
  width: 120px;
  height: 90px;
  flex-shrink: 0;
}

.transition-border {
  transition: all 0.2s ease-in-out;
}

.transition-color {
  transition: color 0.2s ease-in-out;
}

.border-blue {
  border-color: var(--q-blue-8) !important;
  border-width: 2px !important;
}

.hover-blue:hover {
  color: var(--q-blue-8) !important;
}

.auto-detect-hint {
  background: rgba(25, 118, 210, 0.06);
  border-left: 3px solid var(--q-blue-8);
  border-radius: 0 4px 4px 0;
  padding: 4px 8px;
}
</style>
